import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { useAuthStatus, useFilmFeaturePlaylistQuery } from '@mwxltd/flix-api-client'
import { ChatContextProvider } from '@flix/common/domain/group-watch/ChatContext'
import messages from '@flix/common/domain/group-watch/messages'
import { useGroupWatchRoom } from '@flix/common/domain/group-watch/useGroupWatchRoom/useGroupWatchRoom'
import { useAppStateChange, useDimensions } from '@flix/common/hooks'
import { useMediaSelect } from '@flix/common/style/media'
import VideoPlayer from '../../../components/video/VideoPlayer/VideoPlayer'
import { useSnackbar } from '../../../containers/app/Snackbar/SnackbarContext'
import Error404 from '../../../containers/errors/Error404/Error404'
import ErrorPage from '../../../containers/errors/ErrorPage/ErrorPage'

const GroupWatchPlayerPage = ({ history }) => {
  const isWatchingFeature = useRef(false)
  const [featurePlaySuccess, setFeaturePlaySuccess] = useState(false)

  const location = useLocation()
  const query = new URLSearchParams(location.search)
  const groupWatchId = query.get('groupWatchId')

  const { snackbarMsg } = useSnackbar()
  const intl = useIntl()

  const authStatus = useAuthStatus()

  const onGroupIsNotLive = () => {
    history.goBack()
    snackbarMsg(intl.formatMessage(messages.groupNotLive))
  }

  const {
    activeUsers,
    chatMessages,
    groupDetailsLoading,
    groupWatch,
    groupWatchError,
    groupPossiblyInvalid,
    groupHasStarted,
    userIsHost,
    socketConnected,
    userIsWatchingOnTV,
    videoPlaybackStatus,
    websocketHandlers,
  } = useGroupWatchRoom({
    authStatus,
    groupWatchId,
    initialSyncEnabled: featurePlaySuccess,
    onGroupIsNotLive,
    onReconnectFailed: () => {
      history.goBack()
      snackbarMsg(intl.formatMessage(messages.groupDisconnected))
    },
  })

  useEffect(() => {
    // Web app specific handling for a guest when playback hasn't started yet:
    // The guest is taken back to the previous screen rather than seeing a holding page
    if (!groupDetailsLoading && !groupWatchError && !groupHasStarted && !userIsHost) {
      onGroupIsNotLive()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupDetailsLoading, groupWatchError, groupHasStarted, userIsHost])

  // Perform a full browser refresh. This is to ensure correct resynchronisation of the group.
  useAppStateChange({ onForeground: () => !socketConnected() && history.go(0) }, [
    history,
    socketConnected,
  ])

  const filmSlug = groupWatch?.filmSlug

  const {
    loading: filmLoading,
    error: filmError,
    data: { film, playlist = [] },
    refreshUrl,
  } = useFilmFeaturePlaylistQuery({ slug: filmSlug, groupWatchId })

  const { landscape, height, width } = useDimensions()

  const chatWidth = useMediaSelect({
    xs: landscape ? 0 : width,
    md: landscape ? 300 : width,
    lg: landscape ? 400 : width,
  })

  if (groupWatchError) return <Error404 item="screening party" />
  if (filmError) return <ErrorPage error={filmError} />

  if (
    filmLoading ||
    groupDetailsLoading ||
    !groupWatch ||
    !playlist.length ||
    groupPossiblyInvalid
  ) {
    return <div style={{ minHeight: '500px' }} />
  }

  return (
    <ChatContextProvider
      groupWatch={groupWatch}
      sendMessage={websocketHandlers.sendChatMessage}
      chatMessages={chatMessages}
      activeUsers={activeUsers}
      userIsWatchingOnTV={userIsWatchingOnTV}
      onBack={history.goBack}
    >
      <VideoPlayer
        videoHidden={userIsWatchingOnTV}
        playlist={playlist}
        filmSlug={filmSlug}
        filmId={film?.id}
        festivalId={groupWatch?.festivalId}
        errorMetaData={{ film: { slug: filmSlug }, groupWatch: { id: groupWatch.id } }}
        height={height}
        width={width}
        // Group Watch related props
        groupWatchId={groupWatchId}
        groupWatchChatEnabled
        playbackRateChangeEnabled={false}
        featuredActorsEnabled={false}
        userActiveOnKeydown={false}
        playbackStatusOverride={videoPlaybackStatus}
        chatWidth={chatWidth}
        chatPosition={landscape ? 'right' : 'bottom'}
        onPlaySuccess={() => isWatchingFeature.current && setFeaturePlaySuccess(true)}
        onPlaybackStatusChange={websocketHandlers.emitPlaybackStatusChange}
        onRequestRefreshUrl={refreshUrl}
        onRequestClose={history.goBack}
        onQueueNewVideo={(nextVideo) => {
          isWatchingFeature.current = nextVideo.seekTimeType === 'feature'
        }}
      />
    </ChatContextProvider>
  )
}

export default GroupWatchPlayerPage
